import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./VenueShow.css";
import { Grid } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CircularProgress from "@mui/material/CircularProgress";
import BACKOFFICE_BASE_API_V1 from "../../utils/backofficeApi";
import { useAuth } from "../../contexts/AuthContext";
import {
  addFavorite,
  getMyFavorites,
  removeFavorite,
} from "../../utils/favoritesApi";
import { applyDynamicSeo } from "../../utils/dynamicSeo";
import {
  ensureOkResponse,
  handleApiFailureRedirect,
} from "../../utils/apiFailsafe";

function VenueShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authPayload } = useAuth();
  const [venue, setVenue] = useState(null);
  const [products, setProducts] = useState({ products: [] });
  const [loading, setLoading] = useState(true);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [favoriteError, setFavoriteError] = useState("");
  const [favoriteNotice, setFavoriteNotice] = useState("");

  const venueImage =
    venue?.venue?.image_url ||
    "https://placehold.co/600x400/weddings/white";

  useEffect(() => {
    let isMounted = true;

    const loadVenueAndProducts = async () => {
      try {
        setLoading(true);
        const [venueResponse, productsResponse] = await Promise.all([
          fetch(`${BACKOFFICE_BASE_API_V1}/venues/${id}`),
          fetch(`${BACKOFFICE_BASE_API_V1}/venues/${id}/products`),
        ]);

        ensureOkResponse(venueResponse);
        ensureOkResponse(productsResponse);

        const [venueData, productsData] = await Promise.all([
          venueResponse.json(),
          productsResponse.json(),
        ]);

        if (!isMounted) {
          return;
        }

        setVenue(venueData);
        setProducts(productsData || { products: [] });
      } catch (error) {
        if (isMounted) {
          handleApiFailureRedirect(navigate, error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVenueAndProducts();

    return () => {
      isMounted = false;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!favoriteNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFavoriteNotice("");
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [favoriteNotice]);

  useEffect(() => {
    const venueRecord = venue?.venue;
    const venueName = venueRecord?.name;
    if (!venueName) {
      return;
    }

    applyDynamicSeo({
      title: `${venueName} | Kazhavda`,
      description:
        venueRecord.details ||
        "Погледнете детали за свадбена локација и достапни продукти за организација на свадба.",
      imageUrl: venueRecord.image_url,
      type: "business.business",
      keywords: `свадбена локација, ${venueName}, wedding venue, ${venueRecord.city || ""}, kazhavda`,
    });
  }, [venue]);

  useEffect(() => {
    const venueId = venue?.venue?.id || id;

    if (!authPayload?.id_token || !venueId) {
      setIsFavorite(false);
      return undefined;
    }

    let isMounted = true;

    const loadFavoriteState = async () => {
      try {
        setFavoriteError("");
        const payload = await getMyFavorites();
        const venueIdAsString = String(venueId);
        const venueIds = Array.isArray(payload?.venue_ids)
          ? payload.venue_ids.map((value) => String(value))
          : [];
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const fallbackFavorites = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.favorites)
            ? payload.favorites
            : [];

        const favorited =
          venueIds.includes(venueIdAsString) ||
          items.some(
            (item) =>
              item?.favorite_type === "venue" &&
              String(item?.item_id) === venueIdAsString
          ) ||
          fallbackFavorites.some(
            (favorite) =>
              favorite?.favorite_type === "venue" &&
              String(favorite?.item_id) === venueIdAsString
          );

        if (isMounted) {
          setIsFavorite(favorited);
        }
      } catch (error) {
        console.error("Error fetching favorites:", error);
        if (isMounted) {
          setFavoriteError("Неуспешно вчитување на омилениот статус.");
        }
      }
    };

    loadFavoriteState();

    return () => {
      isMounted = false;
    };
  }, [authPayload?.id_token, id, venue?.venue?.id]);

  const handleFavoriteToggle = async () => {
    const venueId = venue?.venue?.id || id;

    if (!authPayload?.id_token || !venueId || favoriteBusy) {
      return;
    }

    const nextFavoriteState = !isFavorite;
    setFavoriteBusy(true);
    setFavoriteError("");
    setIsFavorite(nextFavoriteState);

    try {
      if (nextFavoriteState) {
        await addFavorite({
          favoriteType: "venue",
          itemId: venueId,
        });
      } else {
        await removeFavorite({
          favoriteType: "venue",
          itemId: venueId,
        });
      }

      setFavoriteNotice(
        nextFavoriteState ? "Додадено во омилени." : "Отстрането од омилени."
      );
    } catch (error) {
      console.error("Error updating favorite:", error);
      setIsFavorite(!nextFavoriteState);
      setFavoriteError("Неуспешно ажурирање на омилени.");
    } finally {
      setFavoriteBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="venue-container product-show venue-show">
        <div className="venue-header">
          <div className="venue-header-top">
            <button className="back-button" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          </div>
          <div className="venue-hero">
            <img
              src={"https://placehold.co/600x400/lightgray/lightgray"}
              alt="Кажав Да"
              className="venue-image"
            />
          </div>
          <div className="venue-summary">
            <span className="venue-eyebrow blurred"></span>
            <h1 className="venue-title blurred">Кажав Да</h1>
            <div className="venue-meta">
              <span className="blurred"></span>
              <span className="blurred"></span>
            </div>
            <p className="venue-description blurred"></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="venue-container product-show venue-show">
      {venue && (
        <div className="venue-header">
          <div className="venue-header-top">
            <button className="back-button" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          </div>
          <div className="venue-hero">
            <img
              src={venueImage}
              alt={venue.venue.name}
              className="venue-image"
              onClick={() => setIsImageOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setIsImageOpen(true);
                }
              }}
            />
          </div>
          <div className="venue-summary">
            <div className="venue-summary-top">
              <span className="venue-eyebrow">VENUE</span>
              {authPayload?.id_token ? (
                <button
                  type="button"
                  className={`product-favorite-button ${
                    isFavorite ? "is-active" : ""
                  }`}
                  onClick={handleFavoriteToggle}
                  disabled={favoriteBusy}
                  aria-label={
                    isFavorite ? "Отстрани од омилени" : "Додај во омилени"
                  }
                >
                  {favoriteBusy ? (
                    <CircularProgress size={18} />
                  ) : isFavorite ? (
                    <FavoriteIcon fontSize="small" />
                  ) : (
                    <FavoriteBorderIcon fontSize="small" />
                  )}
                </button>
              ) : null}
            </div>
            <h1 className="venue-title">{venue.venue.name}</h1>
            <div className="venue-meta">
              <span className="venue-location-link">
                {venue.venue.city}
              </span>
              <div className="venue-meta-right">
                {venue.venue.phone_number && (
                  <a
                    className="venue-phone"
                    href={`tel:${String(venue.venue.phone_number).replace(
                      /\\s+/g,
                      ""
                    )}`}
                  >
                    {venue.venue.phone_number}
                  </a>
                )}
              </div>
            </div>
            <p className="venue-description">{venue.venue.details}</p>
            {favoriteError ? (
              <p className="favorite-feedback is-error">{favoriteError}</p>
            ) : null}
            {favoriteNotice ? (
              <p className="favorite-feedback">{favoriteNotice}</p>
            ) : null}
          </div>
        </div>
      )}
      {venue && isImageOpen && (
        <div
          className="image-modal-backdrop"
          onClick={() => setIsImageOpen(false)}
        >
          <div
            className="image-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={() => setIsImageOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <img
              src={venueImage}
              alt={venue.venue.name}
              className="image-modal-image"
            />
          </div>
        </div>
      )}

      {/* Products Section */}
      <div className="products-section">
        <h2 className="products-section-title">Производи</h2>
        <Grid
          container
          className="products-list"
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-start"
          alignItems={{ xs: "center", sm: "flex-start" }}
          wrap="wrap"
          sx={{
            px: { xs: 2, sm: 4 },
          }}
        >
          {Array.isArray(products?.products) && products.products.length > 0 ? (
            products.products.map((product) => (
              <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={product.id}>
                <Link
                  to={`/product/${product.id}?viewed_from=standard`}
                  className="product-card-link"
                >
                  <div className="product-card">
                    <img
                      src={product.images[0] || "https://placehold.co/300x200"}
                      alt={product.name}
                      className="product-image"
                    />
                    <div className="product-card-content">
                      <h3 className="product-title">{product.name}</h3>
                      <p className="product-price">
                        {product.price}
                        <small>ден.</small>
                      </p>
                    </div>
                  </div>
                </Link>
              </Grid>
            ))
          ) : (
            <div className="no-products">
              Нема додадено производи во моментов...
            </div>
          )}
        </Grid>
        <div className="products-action">
          <Link to="/all-products" className="all-products-button">
            Сите производи
          </Link>
        </div>
      </div>
    </div>
  );
}

export default VenueShow;
